import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  CSSProperties,
  ReactNode
} from 'react';
import ReactMapGl, { MapRef } from 'react-map-gl/mapbox';
import {
  EasingOptions,
  LngLatBoundsLike,
  Map as MapInstance,
  MapEvent,
  StyleSpecification
} from 'mapbox-gl';
import { isEqual } from 'lodash';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapContext } from './MapContext';

// The map events react-mapbox-gl forwarded that react-map-gl also emits.
// Handlers keep react-mapbox-gl's (map, event) signature.
const MAP_EVENT_PROPS = [
  'onClick',
  'onDblClick',
  'onMouseDown',
  'onMouseUp',
  'onMouseMove',
  'onMouseOut',
  'onContextMenu',
  'onTouchStart',
  'onTouchEnd',
  'onTouchMove',
  'onTouchCancel',
  'onMoveStart',
  'onMove',
  'onMoveEnd',
  'onDragStart',
  'onDrag',
  'onDragEnd',
  'onZoomStart',
  'onZoom',
  'onZoomEnd',
  'onRotateStart',
  'onRotate',
  'onRotateEnd',
  'onPitchStart',
  'onPitch',
  'onPitchEnd',
  'onBoxZoomStart',
  'onBoxZoomEnd',
  'onBoxZoomCancel',
  'onResize',
  'onRender',
  'onIdle',
  'onError',
  'onData',
  'onStyleData',
  'onSourceData',
  'onWheel'
] as const;

type MapEventProp = typeof MAP_EVENT_PROPS[number];
export type MapEventHandler = (map: MapInstance, event: any) => void;
type MapEventHandlers = { [Key in MapEventProp]?: MapEventHandler };

export interface MapboxMapProps extends MapEventHandlers {
  /** Map style URL or spec. The container's CSS goes in `containerStyle`. */
  style: string | StyleSpecification;
  center?: [number, number];
  zoom?: [number];
  fitBounds?: LngLatBoundsLike;
  fitBoundsOptions?: EasingOptions;
  flyToOptions?: EasingOptions;
  animationOptions?: EasingOptions;
  movingMethod?: 'flyTo' | 'easeTo' | 'jumpTo';
  containerStyle?: CSSProperties;
  className?: string;
  'data-testid'?: string;
  minZoom?: number;
  maxZoom?: number;
  preserveDrawingBuffer?: boolean;
  attributionControl?: boolean;
  onStyleLoad?: (map: MapInstance, event: MapEvent) => void;
  children?: ReactNode;
}

type CameraProps = Pick<
  MapboxMapProps,
  'center' | 'zoom' | 'fitBounds' | 'fitBoundsOptions'
>;

// react-mapbox-gl's defaults, kept so consumers that never passed these see
// the same map they always did.
const DEFAULT_CENTER: [number, number] = [-0.2416815, 51.5285582];
const DEFAULT_ZOOM: [number] = [11];
const DEFAULT_CONTAINER_STYLE: CSSProperties = { textAlign: 'left' };
const ACCESS_TOKEN = "pk.eyJ1Ijoic3RyaWRlcmVuZ2luZWVyaW5nIiwiYSI6ImNsdWs5ZG1vNzA2azYyam1scHljMjNyc3kifQ.YBj-8NtAHftqXroxqmbxXw";

const boundsCenter = (
  bounds: LngLatBoundsLike
): [number, number] | undefined => {
  if (!Array.isArray(bounds)) return undefined;
  if (bounds.length === 4) {
    return [(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2];
  }
  const [southWest, northEast] = bounds;
  if (Array.isArray(southWest) && Array.isArray(northEast)) {
    return [
      (southWest[0] + northEast[0]) / 2,
      (southWest[1] + northEast[1]) / 2
    ];
  }
  return undefined;
};

const MapboxMap = ({
  style,
  center,
  zoom = DEFAULT_ZOOM,
  fitBounds,
  fitBoundsOptions,
  flyToOptions,
  animationOptions,
  movingMethod = 'flyTo',
  containerStyle,
  className,
  'data-testid': testId,
  minZoom = 0,
  maxZoom = 20,
  preserveDrawingBuffer = false,
  attributionControl = true,
  onStyleLoad,
  children,
  ...eventHandlers
}: MapboxMapProps) => {
  const [map, setMap] = useState<MapInstance | null>(null);
  const [loaded, setLoaded] = useState(false);
  const instanceRef = useRef<MapInstance | null>(null);

  // react-mapbox-gl read the camera props once, when it constructed the map,
  // and compared every later render against the previous one.
  const previousCamera = useRef<CameraProps>({
    center,
    zoom,
    fitBounds,
    fitBoundsOptions
  });

  const [initialViewState] = useState(() => {
    const mount = previousCamera.current;
    const initialCenter =
      mount.center ??
      (mount.fitBounds && boundsCenter(mount.fitBounds)) ??
      DEFAULT_CENTER;
    return {
      longitude: initialCenter[0],
      latitude: initialCenter[1],
      zoom: (mount.zoom ?? DEFAULT_ZOOM)[0]
    };
  });

  const handleRef = useCallback((ref: MapRef | null) => {
    if (!ref || instanceRef.current) return;
    const instance = ref.getMap();
    instanceRef.current = instance;
    const mount = previousCamera.current;
    if (mount.fitBounds) {
      instance.fitBounds(mount.fitBounds, mount.fitBoundsOptions);
    }
    setMap(instance);
  }, []);

  useEffect(() => {
    if (!map) return;
    const previous = previousCamera.current;
    previousCamera.current = { center, zoom, fitBounds, fitBoundsOptions };

    if (
      fitBounds &&
      (fitBounds !== previous.fitBounds ||
        !isEqual(fitBoundsOptions, previous.fitBoundsOptions))
    ) {
      // Consumers derive center and bounds from the same geometry, so both
      // change together; the fit must not be overridden by a fly to its own
      // centre at the old zoom.
      map.fitBounds(fitBounds, fitBoundsOptions);
      return;
    }

    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();
    const zoomChanged = zoom !== previous.zoom && zoom[0] !== currentZoom;
    const centerChanged =
      center !== previous.center &&
      !!center &&
      (center[0] !== currentCenter.lng || center[1] !== currentCenter.lat);
    if (!zoomChanged && !centerChanged) return;

    const camera = {
      ...animationOptions,
      ...flyToOptions,
      zoom: zoomChanged ? zoom[0] : currentZoom,
      center: centerChanged && center ? center : currentCenter
    };
    if (movingMethod === 'jumpTo') {
      map.jumpTo(camera);
    } else if (movingMethod === 'easeTo') {
      map.easeTo(camera);
    } else {
      map.flyTo(camera);
    }
  });

  const handleLoad = (event: MapEvent) => {
    setLoaded(true);
    onStyleLoad?.(instanceRef.current ?? event.target, event);
  };

  const callbacks: Partial<Record<MapEventProp, (event: any) => void>> = {};
  MAP_EVENT_PROPS.forEach(name => {
    const handler = eventHandlers[name];
    if (handler) {
      callbacks[name] = event =>
        handler(instanceRef.current ?? event.target, event);
    }
  });

  return (
    <div
      className={className}
      style={{ ...DEFAULT_CONTAINER_STYLE, ...containerStyle }}
      data-testid={testId}
    >
      <ReactMapGl
        ref={handleRef}
        mapboxAccessToken={ACCESS_TOKEN}
        mapStyle={style}
        initialViewState={initialViewState}
        minZoom={minZoom}
        maxZoom={maxZoom}
        preserveDrawingBuffer={preserveDrawingBuffer}
        attributionControl={attributionControl}
        onLoad={handleLoad}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...callbacks}
      >
        {loaded && map && (
          <MapContext.Provider value={map}>{children}</MapContext.Provider>
        )}
      </ReactMapGl>
    </div>
  );
};

export default MapboxMap;
